import React, { useState, useEffect } from "react";
import axios from "axios";
import { ethers } from "ethers";
import { Dialog, DialogTitle, DialogContent, TextField, Button, CircularProgress, IconButton, Checkbox, FormControlLabel, Select, MenuItem, Typography, Box, Chip } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { SERVER_NAME } from './config';

import E3DTokenABI from "./E3DToken_ABI.json";
import NFTManagerABI from "./E3DNFTManager_ABI.json";
import { getSubscriptionDetails } from './web3/contractInteractions';

const E3DToken_ADDRESS = "0x6488861b401F427D13B6619C77C297366bCf6386";
const NFTManager_ADDRESS = "0xeED4620ff525101Ffcf7327378232CA9EF778D47";
const MINT_FEE = ethers.utils.parseUnits("100", 18); // 100 E3D

const MintNFTDialog = ({ open, onClose, setShowMintNFTDialog, userAddress, graphRef, nodes }) => {
  const [nftName, setNftName] = useState("");
  const [nftDescription, setNftDescription] = useState("");
  const [listForSale, setListForSale] = useState(false);
  const [saleAmount, setSaleAmount] = useState("");
  const [saleCurrency, setSaleCurrency] = useState("E3D");
  const [isMinting, setIsMinting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [hasFreeMints, setHasFreeMints] = useState(false);
  const [remainingFreeMints, setRemainingFreeMints] = useState(0);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);

  // Check subscription status when component mounts or userAddress changes
  useEffect(() => {
    const checkSubscription = async () => {
      if (!open || !userAddress) return;
      
      setIsLoadingSubscription(true);
      try {
        const details = await getSubscriptionDetails(userAddress);
        setSubscriptionStatus(details);
        
        if (details && details.isActive && details.remainingMints > 0) {
          setHasFreeMints(true);
          setRemainingFreeMints(details.remainingMints);
        } else {
          setHasFreeMints(false);
          setRemainingFreeMints(0);
        }
      } catch (error) {
        console.error("Error checking subscription:", error);
        setHasFreeMints(false);
      } finally {
        setIsLoadingSubscription(false);
      }
    };
    
    checkSubscription();
  }, [open, userAddress]);

  const mintNFT = async (metadataURI, linkedNFTs) => {
    try {
      if (!window.ethereum) throw new Error("MetaMask is not installed.");
      await window.ethereum.request({ method: "eth_requestAccounts" });

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const nftContract = new ethers.Contract(NFTManager_ADDRESS, NFTManagerABI, signer);
      
      // Only approve token spending if not using a free mint
      if (!hasFreeMints) {
        setStatusMessage("Approving E3D token spending...");
        const e3dToken = new ethers.Contract(E3DToken_ADDRESS, E3DTokenABI, signer);
        const approvalTx = await e3dToken.approve(NFTManager_ADDRESS, MINT_FEE);
        await approvalTx.wait();
        setStatusMessage("Approval complete. Minting NFT...");
      } else {
        setStatusMessage("Using free mint from your premium subscription...");
      }

      const mintTx = await nftContract.mintNFT(metadataURI, linkedNFTs);
      setStatusMessage("Transaction submitted. Waiting for confirmation...");
      const receipt = await mintTx.wait();
      
      // Refresh subscription status after minting
      if (hasFreeMints) {
        const updatedDetails = await getSubscriptionDetails(userAddress);
        setRemainingFreeMints(updatedDetails?.remainingMints || 0);
        setHasFreeMints(updatedDetails?.remainingMints > 0);
      }

      const tokenId = receipt.events?.find(e => e.event === "Transfer")?.args?.tokenId.toString();
      
      // If we have a token ID, store the token mappings
      if (tokenId) {
        await storeNFTTokenMappings(tokenId);
      }

      if (listForSale && tokenId) {
        const price = ethers.utils.parseUnits(saleAmount, 18);
        const paymentCurrency = saleCurrency === "E3D" ? E3DToken_ADDRESS : ethers.constants.AddressZero;

        const listTx = await nftContract.listNFT(tokenId, price, paymentCurrency);
        await listTx.wait();
      }

      return { success: true, tokenId };
    } catch (error) {
      console.error("❌ mintNFT error:", error);
      return { success: false, error: error.message };
    }
  };
  
  // Function to store token mappings in the database
  const storeNFTTokenMappings = async (nftTokenId) => {
    try {
      setStatusMessage("Storing token mappings for visualization...");
      
      // Get all node details from the graph
      const mappings = nodes.map(node => ({
        nft_address: nftTokenId,
        token_address: node.id.split(".")[0],
        x: node.x || 0,
        y: node.y || 0,
        z: node.z || 0,
        token_type: node.type || 'ERC20',
        token_name: node.label || '',
        token_symbol: node.symbol || '',
        token_icon: node.icon || '',
        version: Math.floor(Date.now() / 1000)
      }));
      
      // Send the mappings to the server
      await axios.post(`${SERVER_NAME}/storeNFTTokenMapping`, { mappings });
      console.log(`Successfully stored ${mappings.length} token mappings for NFT ${nftTokenId}`);
      setStatusMessage("Token mappings stored successfully.");
      return true;
    } catch (error) {
      console.error("Failed to store NFT token mappings:", error);
      setStatusMessage("Note: Token mappings storage failed, but NFT was minted.");
      return false;
    }
  };

  const captureGraphAsPNG = async () => {
    if (!graphRef?.current) return null;

    const renderer = graphRef.current.renderer();
    const scene = graphRef.current.scene();
    const camera = graphRef.current.camera();

    renderer.render(scene, camera);

    const gl = renderer.getContext();
    const width = gl.drawingBufferWidth;
    const height = gl.drawingBufferHeight;

    const pixels = new Uint8Array(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");

    const imageData = new ImageData(new Uint8ClampedArray(pixels), width, height);
    context.putImageData(imageData, 0, 0);
    context.translate(0, canvas.height);
    context.scale(1, -1);
    context.drawImage(canvas, 0, 0);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          resolve(null);
        }
      }, "image/png");
    });
  };

  const uploadToIPFS = async (graphImageBlob, name, description) => {
    try {
      const formData = new FormData();
      formData.append("file", graphImageBlob, "graph.png");
      formData.append("name", name);
      formData.append("description", description);

      const response = await axios.post(`${SERVER_NAME}/uploadToIPFS`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        return response.data.metadata;
      } else {
        throw new Error(response.data.error || "Upload failed");
      }
    } catch (error) {
      console.error("❌ uploadToIPFS error:", error);
      return null;
    }
  };

  const handleMint = async () => {
    if (!userAddress) {
      setStatusMessage("Please connect your wallet first.");
      return;
    }

    if (!nftName.trim()) {
      setStatusMessage("NFT Name is required.");
      return;
    }

    if (listForSale) {
      if (!saleAmount || isNaN(saleAmount) || Number(saleAmount) <= 0) {
        setStatusMessage("Sale amount must be a positive number.");
        return;
      }
    }

    setIsMinting(true);
    setStatusMessage("");

    try {
      const graphImageBlob = await captureGraphAsPNG();
      if (!graphImageBlob) throw new Error("Failed to capture graph");

      const metadataURI = await uploadToIPFS(graphImageBlob, nftName, nftDescription);
      if (!metadataURI) throw new Error("Failed to upload metadata");

      const nftAddresses = nodes
        .filter((node) => node.id.includes("."))
        .map((node) => node.id.split(".")[0].toLowerCase())
        .filter((address, index, self) => self.indexOf(address) === index);

      const result = await mintNFT(metadataURI, nftAddresses);

      if (result.success) {
        setStatusMessage("NFT Minted Successfully!");
        setTimeout(() => {
          setShowMintNFTDialog(false);
        }, 1500);
      } else {
        setStatusMessage("Minting failed: " + result.error);
      }
    } catch (error) {
      console.error("❌ handleMint error:", error);
      setStatusMessage("Minting failed.");
    }

    setIsMinting(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        style: {
          backgroundColor: '#f5f7ff',
          color: '#111',
        },
      }}
    >
      <DialogTitle>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Mint New NFT</span>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      {isLoadingSubscription ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={2}>
          <CircularProgress size={24} style={{ color: '#4d79ff' }} />
          <Typography variant="body2" style={{ marginLeft: 10, color: '#111' }}>Checking subscription status...</Typography>
        </Box>
      ) : hasFreeMints ? (
        <Box 
          bgcolor="rgba(79, 195, 247, 0.1)" 
          p={2} 
          mx={3} 
          mb={2} 
          borderLeft="4px solid #4fc3f7"
          borderRadius="4px"
        >
          <Box display="flex" alignItems="center">
            <CheckCircleIcon style={{ color: '#4fc3f7', marginRight: 8 }} />
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              Premium Subscriber Benefit
            </Typography>
          </Box>
          <Typography variant="body2">
            You have <Chip label={remainingFreeMints} size="small" style={{ margin: '0 4px', backgroundColor: '#4fc3f7', color: 'white' }} /> free NFT mints remaining from your premium subscription.
          </Typography>
        </Box>
      ) : null}
      <DialogContent>
        <TextField
          fullWidth
          label="NFT Name"
          value={nftName}
          onChange={(e) => setNftName(e.target.value)}
          margin="dense"
          InputLabelProps={{ style: { color: '#111' } }}
          InputProps={{ style: { color: '#111' } }}
        />
        <TextField
          fullWidth
          label="NFT Description"
          multiline
          rows={3}
          value={nftDescription}
          onChange={(e) => setNftDescription(e.target.value)}
          margin="dense"
          InputLabelProps={{ style: { color: '#111' } }}
          InputProps={{ style: { color: '#111' } }}
        />

        <FormControlLabel
          control={<Checkbox checked={listForSale} onChange={(e) => setListForSale(e.target.checked)} />}
          label="List NFT for Sale After Minting"
        />

        {listForSale && (
          <>
            <TextField
              fullWidth
              label="Sale Amount"
              value={saleAmount}
              onChange={(e) => setSaleAmount(e.target.value)}
              margin="dense"
            />
            <Select
              fullWidth
              value={saleCurrency}
              onChange={(e) => setSaleCurrency(e.target.value)}
              margin="dense"
            >
              <MenuItem value="E3D">E3D Token</MenuItem>
              <MenuItem value="ETH">ETH</MenuItem>
            </Select>
          </>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleMint}
          disabled={isMinting || !nftName.trim()}
          style={{ 
            marginTop: 16,
            backgroundColor: hasFreeMints ? '#4fc3f7' : undefined 
          }}
          startIcon={hasFreeMints ? <CheckCircleIcon /> : null}
        >
          {isMinting ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 10, color: 'white' }} />
              Processing...
            </>
          ) : (
            hasFreeMints ? `Mint NFT (Free)` : `Mint NFT (100 E3D)`
          )}
        </Button>

        {statusMessage && (
          <Typography variant="body2" style={{ marginTop: 15, color: statusMessage.includes("failed") ? "red" : "#1f3fff" }}>
            {statusMessage}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MintNFTDialog;
